import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import ModalLayout from "../layouts/ModalLayout";
import { AppDispatch, RootState } from "../../store";
import { AssignClassSubjectPayload } from "../../types/classesTypes";
import { useSelector } from "react-redux";
import { fetchStaff } from "../../features/staff/staffSlice";
import { useParams } from "react-router-dom";
import classesApis from "../../api/classesApis";
import { useBranchCode } from "../../util/hooks/useBranch";

interface Props {
  subjectId?: number | null;
  onClose?: () => void;
  onSuccess?: () => void;
}

function AddSubjectToClass({ onClose, onSuccess, subjectId }: Props) {
  const { classId } = useParams<{ classId: string }>();
  const parsedClassId = Number(classId);
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState<AssignClassSubjectPayload>({
    subjectId: subjectId || "",
    staffId: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const { staffList, filters } = useSelector((state: RootState) => state.staff);
  const branchCode = useBranchCode();

  const { subjectEnabled } = useSelector((state: RootState) => state.access);

  const { classes } = useSelector((state: RootState) => state.classes);

  const className = useMemo(() => {
    return classes.find((cls) => cls.classId === parsedClassId)?.className;
  }, [classes, parsedClassId]);

  const subjects = useMemo(
    () => subjectEnabled.filter((subject) => subject.active),
    [subjectEnabled],
  );

  const selectedSubject = useMemo(() => {
    return subjects.find((s) => Number(s.subjectId) === Number(subjectId));
  }, [subjects, subjectId]);

  useEffect(() => {
    if (subjectId) {
      setFormData((prev) => ({
        ...prev,
        subjectId,
      }));
    }
  }, [subjectId]);


  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        ...filters,
        className,
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [dispatch, filters, branchCode, className]);

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.staffId || !formData.subjectId) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      if (subjectId) {
        await classesApis.updateSubjectStaff(
          formData.subjectId as number,
          formData.staffId as number,
        );
      } else {
        await classesApis.assignSubjectToClass(parsedClassId, {
          ...formData,
        });
      }

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalLayout onClose={onClose}>
      <form className="flex flex-col gap-8 max-w-xl" onSubmit={handleSubmit}>
        {subjectId ? (
          <div>
            <h2 className="text-xl font-semibold text-neutral-400 mb-4">
              Edit Staff
            </h2>
            <label className="text-[13px] font-semibold text-neutral-400 flex items-center">
              Subject
            </label>
            <div className="p-2 px-5 mt-1 text-neutral-300 rounded bg-background-200">
              {selectedSubject?.subjectName || "Loading subject..."}
            </div>
          </div>
        ) : (
          <CustomSelect
            name="subjectId"
            label="Subject"
            value={formData.subjectId}
            placeholder="Select Subject"
            onChange={handleInputChange}
            options={subjects.map((subject) => ({
              label: subject.subjectName,
              value: subject.subjectId,
            }))}
          />
        )}

        <CustomSelect
          name="staffId"
          label="Assign Staff"
          value={formData.staffId}
          placeholder="Select staff"
          onChange={handleInputChange}
          options={
            staffList.length > 0
              ? staffList.map((staff) => ({
                  label: staff.fullName,
                  value: staff.staffId,
                }))
              : [
                  {
                    label: "No teachers available for this class",
                    value: "",
                  },
                ]
          }
        />

        <div className="flex justify-end gap-5">
          <CustomButton
            color="bg-neutral-200"
            text="Cancel"
            onClick={onClose}
          />
          <CustomButton
            color="bg-success-600"
            text={subjectId ? "Update Staff" : "Add"}
            type="submit"
          />
        </div>
      </form>
    </ModalLayout>
  );
}

export default AddSubjectToClass;
